import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Mail, Clock, Calendar, MailOpen, AlertCircle, CheckCircle2, XCircle, Eye, Zap, Info, Edit3, Save, X, RotateCcw, User, ChevronDown, Search, Link2, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/components/app/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { formatInAppTz, tzLabel } from "@/lib/timezone";
import EmailEngagementStats from "@/components/app/EmailEngagementStats";

const TRIGGER_LABEL = {
  time_after_signup: { icon: Clock,    text: "X זמן אחרי הרשמה" },
  fixed_date:        { icon: Calendar, text: "תאריך מוגדר" },
  after_email_opened:{ icon: MailOpen, text: "אחרי שמייל קודם נפתח" },
};

/**
 * A template is considered "welcome / auto-response" if it fires within
 * 5 minutes of signup — i.e. what the user perceives as "the automatic email
 * that gets sent to new leads". Used to badge these prominently in the list
 * so admins can easily find them and edit their content.
 */
function isWelcomeTemplate(t) {
  if (t.trigger_type !== "time_after_signup") return false;
  const m = Number((t.trigger_value || {}).offset_minutes || 0);
  return m <= 5 && t.is_active;
}

const STATUS_BADGE = {
  scheduled: { label: "מתוזמן", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  sent:      { label: "נשלח",   cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  opened:    { label: "נפתח",   cls: "bg-blue-50 text-blue-700 border-blue-200" },
  failed:    { label: "נכשל",   cls: "bg-red-50 text-red-700 border-red-200" },
  skipped:   { label: "דולג",   cls: "bg-stone-50 text-stone-600 border-stone-200" },
};

function formatTrigger(t) {
  const v = t.trigger_value || {};
  if (t.trigger_type === "time_after_signup") {
    const m = Number(v.offset_minutes || 0);
    if (m < 60) return `${m} דקות אחרי הרשמה`;
    if (m < 1440) return `${Math.round(m / 60)} שעות אחרי הרשמה`;
    return `${Math.round(m / 1440)} ימים אחרי הרשמה`;
  }
  if (t.trigger_type === "fixed_date") {
    return v.send_at ? formatInAppTz(v.send_at) : "לא הוגדר תאריך";
  }
  if (t.trigger_type === "after_email_opened") {
    return `${v.delay_minutes || 0} דק' אחרי פתיחה`;
  }
  return "—";
}

export default function AdminEmails() {
  const [tab, setTab] = useState("by-lead"); // 'by-lead' | 'templates' | 'sends' | 'named-links' | 'settings'
  const [templates, setTemplates] = useState([]);
  const [sends, setSends] = useState([]);
  const [byLead, setByLead] = useState([]);
  const [stats, setStats] = useState({ by_template: {}, by_broadcast: {}, by_drip: {} });
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [t, s, bl, statsRes, st] = await Promise.all([
          api.get("/admin/email/templates"),
          api.get("/admin/email/sends?limit=100"),
          api.get("/admin/email/by-lead?limit=300"),
          api.get("/admin/email/stats/summary"),
          api.get("/admin/email/settings"),
        ]);
        setTemplates(t.data);
        setSends(s.data);
        setByLead(bl.data);
        setStats(statsRes.data || { by_template: {}, by_broadcast: {}, by_drip: {} });
        setSettings(st.data);
      } catch (err) {
        console.error("Failed to load email data", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const templateById = Object.fromEntries(templates.map((t) => [t.id, t]));

  return (
    <AdminLayout title="מיילים">
      {/* Resend status banner */}
      {settings && !settings.resend_api_key_set && (
        <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-bold text-amber-900">Resend לא מחובר</div>
            <div className="text-amber-800 mt-1">
              עדיין לא הוגדר <code className="px-1 bg-amber-100 rounded">RESEND_API_KEY</code> ב-Backend. ניתן ליצור ולערוך templates, אבל מיילים בפועל לא יישלחו עד שיתווסף המפתח.
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-5 bg-stone-100 rounded-2xl p-1 w-fit">
        <TabBtn active={tab === "by-lead"} onClick={() => setTab("by-lead")} count={byLead.length} testId="tab-by-lead">לפי ליד</TabBtn>
        <TabBtn active={tab === "templates"} onClick={() => setTab("templates")} count={templates.length} testId="tab-templates">Templates</TabBtn>
        <TabBtn active={tab === "sends"} onClick={() => setTab("sends")} count={sends.length} testId="tab-sends">היסטוריית שליחות</TabBtn>
        <TabBtn active={tab === "named-links"} onClick={() => setTab("named-links")} testId="tab-named-links">לינקי אפיליאייט</TabBtn>
        <TabBtn active={tab === "settings"} onClick={() => setTab("settings")} testId="tab-settings">הגדרות שולח</TabBtn>
      </div>

      {tab === "by-lead" && <ByLeadPanel data={byLead} loading={loading} templateById={templateById} />}

      {tab === "templates" && (
        <>
          {/* Welcome-email discoverability banner */}
          {!loading && templates.length > 0 && (() => {
            const welcomes = templates.filter(isWelcomeTemplate);
            if (welcomes.length === 0) {
              return (
                <div className="mb-5 rounded-2xl border border-sky-200 bg-sky-50 p-4 flex items-start gap-3" data-testid="no-welcome-banner">
                  <Info className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <div className="font-bold text-sky-900">אין כרגע מייל אוטומטי לנרשמים חדשים</div>
                    <div className="text-sky-800 mt-1">
                      צור Template חדש עם trigger <b>&quot;X זמן אחרי הרשמה&quot;</b> וזמן 0 דקות — הוא יישלח באופן אוטומטי לכל ליד חדש (כולל נרשמי VOD מהעמוד <code className="ltr bg-sky-100 px-1 rounded">/vod</code>).
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3" data-testid="welcome-banner">
                <Zap className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm flex-1">
                  <div className="font-bold text-emerald-900">מיילים אוטומטיים לנרשמים חדשים</div>
                  <div className="text-emerald-800 mt-1">
                    כשליד חדש נרשם (בכל אחד מהמשפכים — כולל <code className="ltr bg-emerald-100 px-1 rounded">/vod</code>) — {welcomes.length === 1 ? "התבנית הבאה" : `${welcomes.length} התבניות הבאות`} נשלחות אוטומטית. לחץ על תבנית כדי לערוך את התוכן שלה (כולל ה-magic-link לכניסה לאזור החברים):
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {welcomes.map((w) => (
                      <Link
                        key={w.id}
                        to={`/admin/emails/${w.id}`}
                        className="inline-flex items-center gap-1.5 bg-white border border-emerald-300 hover:border-emerald-500 hover:bg-emerald-100 rounded-full px-3 py-1 text-xs font-bold text-emerald-900 transition-colors"
                        data-testid={`welcome-shortcut-${w.id}`}
                      >
                        <Zap className="w-3 h-3" />
                        {w.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-stone-500">תבניות מייל ואוטומציות. גרירת template אינה נתמכת — סדר השליחה נקבע ע&quot;י ה-trigger.</p>
            <Link to="/admin/emails/new" data-testid="new-template-btn">
              <Button className="h-11 rounded-full bg-brand-charcoal hover:bg-stone-800 text-white">
                <Plus className="w-4 h-4 me-2" />Template חדש
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="py-12 text-center text-stone-500">טוען...</div>
          ) : templates.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="templates-grid">
              {templates.map((t) => {
                const Trigger = TRIGGER_LABEL[t.trigger_type] || TRIGGER_LABEL.time_after_signup;
                const TriggerIcon = Trigger.icon;
                const isWelcome = isWelcomeTemplate(t);
                return (
                  <Link
                    key={t.id}
                    to={`/admin/emails/${t.id}`}
                    data-testid={`template-card-${t.id}`}
                    className={`bg-white rounded-2xl p-5 border ${isWelcome ? "border-emerald-300 ring-2 ring-emerald-100" : t.is_active ? "border-stone-200" : "border-stone-200 opacity-60"} hover:shadow-md hover:-translate-y-0.5 transition-all relative`}
                  >
                    {isWelcome && (
                      <span
                        className="absolute -top-2 -end-2 inline-flex items-center gap-1 bg-emerald-600 text-white text-[10px] font-black px-2 py-0.5 rounded-full shadow-sm"
                        data-testid={`auto-badge-${t.id}`}
                        title="נשלח אוטומטית לכל ליד חדש מיד אחרי הרשמה"
                      >
                        <Zap className="w-3 h-3" />
                        אוטומטי לנרשמים
                      </span>
                    )}
                    <div className="flex items-start justify-between mb-3">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${t.group === 1 ? "bg-amber-50 text-brand-amber border border-amber-200" : "bg-indigo-50 text-indigo-700 border border-indigo-200"}`}>
                        קבוצה {t.group}
                      </span>
                      <span className={`text-xs font-bold ${t.is_active ? "text-emerald-600" : "text-stone-400"}`}>
                        {t.is_active ? "פעיל" : "כבוי"}
                      </span>
                    </div>
                    <h3 className="font-[800] mb-1 text-base">{t.name}</h3>
                    <p className="text-sm text-stone-500 line-clamp-2 mb-3">{t.subject}</p>
                    {(t.campaign_ids && t.campaign_ids.length > 0) && (
                      <div className="text-[10px] font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1 mb-2" title="מוגבל לקמפיינים ספציפיים">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-600"></span>
                        רק ל-{t.campaign_ids.length} {t.campaign_ids.length === 1 ? "קמפיין" : "קמפיינים"}
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-xs text-stone-500">
                      <TriggerIcon className="w-3.5 h-3.5" />
                      <span>{formatTrigger(t)}</span>
                    </div>
                    <div className="mt-4 pt-3 border-t border-stone-100">
                      <EmailEngagementStats stats={stats.by_template[t.id]} testId={`template-stats-${t.id}`} />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </>
      )}

      {tab === "sends" && (
        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
          <table className="w-full text-sm" data-testid="sends-table">
            <thead className="bg-stone-50 border-b border-stone-200 text-right">
              <tr>
                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">סטטוס</th>
                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">Template</th>
                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">נמען</th>
                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">מתוזמן ל</th>
                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">נשלח</th>
                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">נפתח</th>
              </tr>
            </thead>
            <tbody>
              {sends.length === 0 ? (
                <tr><td colSpan={6} className="py-12 text-center text-stone-500">אין שליחות עדיין.</td></tr>
              ) : sends.map((s) => {
                const badge = STATUS_BADGE[s.status] || { label: s.status, cls: "bg-stone-50 text-stone-600" };
                return (
                  <tr key={s.id} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`send-${s.id}`}>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${badge.cls}`}>{badge.label}</span>
                      {s.error && <div className="text-xs text-red-600 mt-1 max-w-[200px] truncate" title={s.error}>{s.error}</div>}
                    </td>
                    <td className="px-4 py-3 font-bold">{templateById[s.template_id]?.name || s.template_id}</td>
                    <td className="px-4 py-3 ltr text-left">{s.to_email}</td>
                    <td className="px-4 py-3 text-xs text-stone-500">{s.scheduled_for ? formatInAppTz(s.scheduled_for) : "—"}</td>
                    <td className="px-4 py-3 text-xs text-stone-500">{s.sent_at ? formatInAppTz(s.sent_at) : "—"}</td>
                    <td className="px-4 py-3 text-xs">
                      {s.opened_at ? (
                        <span className="inline-flex items-center gap-1 text-blue-600 font-bold"><Eye className="w-3.5 h-3.5" />{formatInAppTz(s.opened_at)}</span>
                      ) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {tab === "named-links" && <NamedLinksAnalyticsPanel />}

      {tab === "settings" && settings && <SettingsPanel settings={settings} setSettings={setSettings} />}
    </AdminLayout>
  );
}

function TabBtn({ active, onClick, children, count, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-4 py-2 rounded-xl text-sm font-bold transition ${active ? "bg-white shadow-sm text-brand-charcoal" : "text-stone-500 hover:text-stone-700"}`}
    >
      {children}
      {typeof count === "number" && (
        <span className={`ms-1.5 text-xs ${active ? "text-stone-400" : "text-stone-400"}`}>({count})</span>
      )}
    </button>
  );
}

function EmptyState() {
  return (
    <div className="bg-white rounded-2xl p-12 border border-dashed border-stone-300 text-center" data-testid="templates-empty">
      <Mail className="w-12 h-12 text-stone-300 mx-auto mb-4" />
      <h3 className="font-[800] mb-2">עדיין אין תבניות מייל</h3>
      <p className="text-sm text-stone-500 mb-6 max-w-md mx-auto">
        תבנית מייל ראשונה תישלח אוטומטית לכל נרשם חדש, עם לינק האפיליאייט הספציפי לקמפיין שממנו הוא הגיע.
      </p>
      <Link to="/admin/emails/new">
        <Button className="h-11 rounded-full bg-brand-charcoal text-white">
          <Plus className="w-4 h-4 me-2" />צור Template ראשון
        </Button>
      </Link>
    </div>
  );
}

/**
 * NamedLinksAnalyticsPanel — per-`{{link:KEY}}` click breakdown.
 * Fetches `/admin/email/named-links-analytics?days=N`, renders KPI cards,
 * a per-key table (with expandable campaign breakdown + fallback warnings),
 * and a "keys defined but no clicks yet" section to help admins spot dead offers.
 */
function NamedLinksAnalyticsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [expanded, setExpanded] = useState({});

  const load = async (window_days) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/email/named-links-analytics?days=${window_days}`);
      setData(data);
    } catch (e) {
      console.error("named-links-analytics load failed", e);
      toast.error("שגיאה בטעינת האנליטיקות");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); }, [days]);

  if (loading) return <div className="py-12 text-center text-stone-500" data-testid="named-links-loading">טוען אנליטיקות...</div>;
  if (!data) return null;

  const summary = data.summary || {};
  const rows = data.by_key || [];
  const catalog = data.keys_catalog || [];
  const noClicksKeys = catalog.filter((c) => !c.has_clicks);
  const totalNamed = summary.named_clicks || 0;

  return (
    <div className="space-y-5" data-testid="named-links-analytics-panel">
      {/* Header + window selector */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-[800] text-lg flex items-center gap-2">
            <Link2 className="w-5 h-5 text-brand-amber" />
            ביצועי לינקי אפיליאייט לפי שם
          </h2>
          <p className="text-sm text-stone-500 mt-1">
            אילו הצעות (<code className="text-xs ltr bg-stone-100 px-1 rounded">{`{{link:KEY}}`}</code>) הביאו הכי הרבה קליקים מהמיילים ב-{days} הימים האחרונים.
          </p>
        </div>
        <div className="flex items-center gap-2 bg-stone-100 rounded-full p-1" data-testid="named-links-window">
          {[7, 30, 90, 365].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`window-${d}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${days === d ? "bg-white shadow-sm text-brand-charcoal" : "text-stone-500 hover:text-stone-700"}`}
            >
              {d === 365 ? "שנה" : `${d} ימים`}
            </button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          label="קליקים ללינקים בעלי שם"
          value={summary.named_clicks || 0}
          hint={`מתוך ${summary.total_clicks || 0} סה״כ`}
          testId="kpi-named-clicks"
          accent="amber"
        />
        <KpiCard
          label="לידים ייחודיים שלחצו"
          value={summary.unique_leads || 0}
          hint="בכל סוגי הלינקים"
          testId="kpi-unique-leads"
          accent="blue"
        />
        <KpiCard
          label="מפתחות פעילים"
          value={summary.keys_with_clicks || 0}
          hint={`${summary.keys_defined || 0} הוגדרו במערכת`}
          testId="kpi-keys-active"
          accent="emerald"
        />
        <KpiCard
          label="קליקים ב-fallback"
          value={summary.fallback_clicks || 0}
          hint="ליד ללא KEY מוגדר → לינק ברירת מחדל"
          testId="kpi-fallback-clicks"
          accent={summary.fallback_clicks > 0 ? "orange" : "stone"}
        />
      </div>

      {/* Table */}
      {rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-stone-300 p-12 text-center" data-testid="named-links-empty">
          <TrendingUp className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <h3 className="font-[800] mb-1">עדיין אין קליקים ללינקים בעלי שם</h3>
          <p className="text-sm text-stone-500 max-w-md mx-auto leading-[1.6]">
            הוסף לינקים ל-funnel (Admin → קמפיינים → לינקי אפיליאייט נוספים) והכנס את ה-placeholder <code className="ltr text-xs bg-stone-100 px-1 rounded">{`{{link:KEY}}`}</code> לתבניות המייל.
            הקליקים יופיעו כאן אחרי שהלידים יתחילו ללחוץ.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
          <table className="w-full text-sm" data-testid="named-links-table">
            <thead className="bg-stone-50 border-b border-stone-200 text-right">
              <tr>
                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500 w-6"></th>
                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">מפתח / שם הצעה</th>
                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">קליקים</th>
                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">לידים ייחודיים</th>
                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">% מכלל הבעלי-שם</th>
                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">Fallback</th>
                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">קליק אחרון</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const pct = totalNamed > 0 ? Math.round((r.clicks / totalNamed) * 100) : 0;
                const isOpen = !!expanded[r.link_key];
                return (
                  <NamedLinkRow
                    key={r.link_key}
                    row={r}
                    pct={pct}
                    isOpen={isOpen}
                    onToggle={() => setExpanded((s) => ({ ...s, [r.link_key]: !s[r.link_key] }))}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Keys defined but no clicks yet */}
      {noClicksKeys.length > 0 && (
        <div className="bg-sky-50 border border-sky-200 rounded-2xl p-5" data-testid="named-links-no-clicks">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-[800] text-sky-900 mb-1">
                {noClicksKeys.length} {noClicksKeys.length === 1 ? "מפתח מוגדר אך ללא קליקים" : "מפתחות מוגדרים אך ללא קליקים"} בחלון זה
              </div>
              <div className="text-sm text-sky-800 mb-3 leading-[1.6]">
                המפתחות הבאים מוגדרים ב-funnels אך לא קיבלו קליקים. וודא שאתה משתמש ב-<code className="ltr text-xs bg-white/60 px-1 rounded">{`{{link:KEY}}`}</code> בתבניות המייל, או שקול להסיר אותם.
              </div>
              <div className="flex flex-wrap gap-2">
                {noClicksKeys.map((c) => (
                  <span
                    key={c.key}
                    className="inline-flex items-center gap-1 bg-white border border-sky-300 rounded-full px-3 py-1 text-xs"
                    data-testid={`no-clicks-key-${c.key}`}
                  >
                    <code className="ltr font-mono text-sky-700">{c.key}</code>
                    <span className="text-stone-500">·</span>
                    <span className="text-stone-700">{c.label}</span>
                    <span className="text-stone-400 text-[10px] ms-1">({c.campaigns.length})</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, hint, testId, accent = "stone" }) {
  const colorMap = {
    amber: "bg-amber-50 border-amber-200 text-brand-amber",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    orange: "bg-orange-50 border-orange-200 text-orange-700",
    stone: "bg-stone-50 border-stone-200 text-stone-700",
  };
  return (
    <div className={`rounded-2xl border p-4 ${colorMap[accent]}`} data-testid={testId}>
      <div className="text-[11px] font-bold uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-3xl font-[800] mt-1">{Number(value).toLocaleString()}</div>
      {hint && <div className="text-[11px] opacity-70 mt-1 leading-[1.4]">{hint}</div>}
    </div>
  );
}

function NamedLinkRow({ row, pct, isOpen, onToggle }) {
  const r = row;
  return (
    <>
      <tr
        className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer"
        onClick={onToggle}
        data-testid={`named-link-row-${r.link_key}`}
      >
        <td className="px-4 py-3">
          <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <code className="ltr text-xs bg-amber-50 text-brand-amber font-mono px-2 py-0.5 rounded border border-amber-200">{r.link_key}</code>
            {!r.is_defined_globally && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-800 border border-orange-200" title="הקליק הגיע ממייל אבל המפתח הזה כבר לא מוגדר על שום funnel — כדאי להוסיף אותו או להסיר את ה-placeholder מהתבנית">
                לא מוגדר יותר
              </span>
            )}
          </div>
          <div className="text-xs text-stone-500 mt-0.5">{r.label}</div>
        </td>
        <td className="px-4 py-3 font-bold text-brand-amber">{r.clicks.toLocaleString()}</td>
        <td className="px-4 py-3 font-bold text-blue-700">{r.unique_leads.toLocaleString()}</td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="w-20 h-2 bg-stone-100 rounded-full overflow-hidden">
              <div className="h-full bg-brand-amber" style={{ width: `${pct}%` }}></div>
            </div>
            <span className="text-xs text-stone-600 font-bold">{pct}%</span>
          </div>
        </td>
        <td className="px-4 py-3">
          {r.fallback_clicks > 0 ? (
            <span className="text-xs font-bold text-orange-700 bg-orange-50 border border-orange-200 rounded-full px-2 py-0.5" title="קליקים שמקורם בליד ב-funnel שלא הגדיר את המפתח הזה — נשלח לינק ברירת מחדל של ה-funnel">
              {r.fallback_clicks} קליקים
            </span>
          ) : (
            <span className="text-xs text-stone-400">—</span>
          )}
        </td>
        <td className="px-4 py-3 text-xs text-stone-500">
          {r.last_click ? formatInAppTz(r.last_click) : "—"}
        </td>
      </tr>
      {isOpen && (
        <tr className="bg-stone-50/50">
          <td colSpan={7} className="px-4 py-3">
            <div className="ps-6">
              <div className="text-xs font-bold text-stone-500 mb-2">פירוט לפי funnel:</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {r.campaigns.map((c) => (
                  <div
                    key={c.campaign_id || "unknown"}
                    className={`flex items-center justify-between rounded-lg px-3 py-2 border ${c.is_fallback ? "bg-orange-50 border-orange-200" : "bg-white border-stone-200"}`}
                    data-testid={`named-link-camp-${r.link_key}-${c.campaign_slug || "unknown"}`}
                  >
                    <div>
                      <div className="text-sm font-bold text-stone-800">
                        {c.campaign_name || "—"}
                        {c.is_fallback && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-orange-200 text-orange-900 ms-2">
                            fallback
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-stone-500 ltr">{c.campaign_slug || "—"}</div>
                    </div>
                    <div className="font-bold text-brand-amber">{c.clicks}</div>
                  </div>
                ))}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}


function ByLeadPanel({ data, loading, templateById }) {
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState({});
  const [statusFilter, setStatusFilter] = useState("all"); // all|opened|not_opened|failed|received
  const [listFilter, setListFilter] = useState("all");
  const [templateFilter, setTemplateFilter] = useState("all");
  const [sort, setSort] = useState("recent"); // recent|name|most_sends|most_opens|most_failures|signup

  // Distinct list_ids present in the data — feeds the List dropdown
  const listOptions = useMemo(() => {
    const s = new Set();
    for (const r of data) if (r.list_id) s.add(r.list_id);
    return Array.from(s).sort();
  }, [data]);

  const templateOptions = useMemo(() => {
    const m = new Map();
    for (const r of data) for (const s of r.sends || []) {
      if (s.template_id) m.set(s.template_id, s.template_name || templateById[s.template_id]?.name || s.template_id);
    }
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [data, templateById]);

  const filtered = useMemo(() => {
    let out = data;

    // Text search
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      out = out.filter((r) =>
        (r.name || "").toLowerCase().includes(q) ||
        (r.email || "").toLowerCase().includes(q) ||
        (r.list_id || "").toLowerCase().includes(q)
      );
    }

    // List filter
    if (listFilter !== "all") {
      out = out.filter((r) => (r.list_id || "") === listFilter);
    }

    // Template filter — keep lead only if they received THAT template at least once
    if (templateFilter !== "all") {
      out = out.filter((r) => (r.sends || []).some((s) => s.template_id === templateFilter));
    }

    // Status quick filter
    switch (statusFilter) {
      case "opened":
        out = out.filter((r) => r.totals.opened > 0);
        break;
      case "not_opened":
        out = out.filter((r) => r.totals.opened === 0 && r.totals.sent > 0);
        break;
      case "failed":
        out = out.filter((r) => r.totals.failed > 0);
        break;
      case "received":
        out = out.filter((r) => r.totals.sent > 0);
        break;
      default:
        break;
    }

    // Sort
    const arr = [...out];
    switch (sort) {
      case "name":
        arr.sort((a, b) => (a.name || "").localeCompare(b.name || "", "he"));
        break;
      case "most_sends":
        arr.sort((a, b) => b.totals.sent - a.totals.sent);
        break;
      case "most_opens":
        arr.sort((a, b) => b.totals.opened - a.totals.opened);
        break;
      case "most_failures":
        arr.sort((a, b) => b.totals.failed - a.totals.failed);
        break;
      case "signup":
        arr.sort((a, b) => (b.signed_up_at || "").localeCompare(a.signed_up_at || ""));
        break;
      default: // recent
        arr.sort((a, b) => (b.last_activity_at || "").localeCompare(a.last_activity_at || ""));
    }
    return arr;
  }, [data, query, listFilter, templateFilter, statusFilter, sort]);

  const clearFilters = () => {
    setQuery("");
    setStatusFilter("all");
    setListFilter("all");
    setTemplateFilter("all");
    setSort("recent");
  };

  // Counts for the chip badges (per pill it shows what the filter would leave)
  const chipCounts = useMemo(() => {
    const base = data; // chips show counts BEFORE the chip filter is applied
    return {
      all: base.length,
      opened: base.filter((r) => r.totals.opened > 0).length,
      not_opened: base.filter((r) => r.totals.opened === 0 && r.totals.sent > 0).length,
      failed: base.filter((r) => r.totals.failed > 0).length,
      received: base.filter((r) => r.totals.sent > 0).length,
    };
  }, [data]);

  const totals = useMemo(() => {
    return filtered.reduce(
      (acc, r) => {
        acc.total += r.totals.total;
        acc.sent += r.totals.sent;
        acc.opened += r.totals.opened;
        acc.failed += r.totals.failed;
        acc.leadsWithSends += 1;
        if (r.totals.opened > 0) acc.leadsThatOpened += 1;
        return acc;
      },
      { total: 0, sent: 0, opened: 0, failed: 0, leadsWithSends: 0, leadsThatOpened: 0 }
    );
  }, [filtered]);

  const activeFilterCount =
    (query.trim() ? 1 : 0) +
    (listFilter !== "all" ? 1 : 0) +
    (templateFilter !== "all" ? 1 : 0) +
    (statusFilter !== "all" ? 1 : 0) +
    (sort !== "recent" ? 1 : 0);

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  if (loading) {
    return <div className="p-12 text-center text-stone-400 text-sm">טוען...</div>;
  }

  return (
    <>
      {/* Header stats — reflect the CURRENT filtered view */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <MiniStat label="לידים בתצוגה" value={totals.leadsWithSends} testId="by-lead-stat-leads" />
        <MiniStat label="לידים שפתחו" value={totals.leadsThatOpened} sub={totals.leadsWithSends ? `${Math.round((totals.leadsThatOpened / totals.leadsWithSends) * 100)}%` : "—"} testId="by-lead-stat-openers" />
        <MiniStat label="מיילים שנשלחו" value={totals.sent} testId="by-lead-stat-sent" />
        <MiniStat label="פתיחות" value={totals.opened} testId="by-lead-stat-opened" />
      </div>

      {/* Filters card */}
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm mb-4 overflow-hidden" data-testid="by-lead-filters">
        {/* Quick status chips */}
        <div className="p-3 md:p-4 border-b border-stone-100 flex items-center gap-2 flex-wrap">
          <StatusChip active={statusFilter === "all"} onClick={() => setStatusFilter("all")} testId="chip-all">
            הכל <span className="opacity-60 me-1">({chipCounts.all})</span>
          </StatusChip>
          <StatusChip active={statusFilter === "received"} onClick={() => setStatusFilter("received")} testId="chip-received" color="emerald">
            קיבלו <span className="opacity-60 me-1">({chipCounts.received})</span>
          </StatusChip>
          <StatusChip active={statusFilter === "opened"} onClick={() => setStatusFilter("opened")} testId="chip-opened" color="blue">
            פתחו <span className="opacity-60 me-1">({chipCounts.opened})</span>
          </StatusChip>
          <StatusChip active={statusFilter === "not_opened"} onClick={() => setStatusFilter("not_opened")} testId="chip-not-opened" color="amber">
            קיבלו ולא פתחו <span className="opacity-60 me-1">({chipCounts.not_opened})</span>
          </StatusChip>
          <StatusChip active={statusFilter === "failed"} onClick={() => setStatusFilter("failed")} testId="chip-failed" color="red">
            נכשלו <span className="opacity-60 me-1">({chipCounts.failed})</span>
          </StatusChip>
        </div>

        {/* Dropdowns + Search */}
        <div className="p-3 md:p-4 flex items-center gap-2 flex-wrap">
          <FilterSelect label="מיון" value={sort} onChange={setSort} testId="sort-select">
            <option value="recent">פעילות אחרונה</option>
            <option value="signup">תאריך הרשמה</option>
            <option value="name">שם (א-ת)</option>
            <option value="most_sends">הכי הרבה שליחות</option>
            <option value="most_opens">הכי הרבה פתיחות</option>
            <option value="most_failures">הכי הרבה כשלונות</option>
          </FilterSelect>

          <FilterSelect label="List" value={listFilter} onChange={setListFilter} testId="list-select">
            <option value="all">כל הרשימות</option>
            {listOptions.map((l) => <option key={l} value={l}>{l}</option>)}
          </FilterSelect>

          <FilterSelect label="Template" value={templateFilter} onChange={setTemplateFilter} testId="template-select">
            <option value="all">כל ה-Templates</option>
            {templateOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </FilterSelect>

          <div className="flex items-center gap-2 flex-1 min-w-[220px] bg-stone-50 border border-stone-200 rounded-lg px-3">
            <Search className="w-4 h-4 text-stone-400 flex-shrink-0" />
            <Input
              data-testid="by-lead-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="חיפוש שם, אימייל..."
              className="h-9 border-0 shadow-none focus-visible:ring-0 px-0 bg-transparent"
            />
          </div>

          <span className="text-xs text-stone-500 font-bold px-2" data-testid="by-lead-count">
            {filtered.length}/{data.length}
          </span>

          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={clearFilters}
              data-testid="clear-filters"
              className="text-xs font-bold text-brand-amber hover:text-brand-amberHover inline-flex items-center gap-1 px-2"
              title="נקה סינונים"
            >
              <X className="w-3.5 h-3.5" />
              נקה ({activeFilterCount})
            </button>
          )}
        </div>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center text-stone-400 text-sm">
          {data.length === 0 ? "עדיין לא נשלחו מיילים ללידים." : "אין תוצאות לסינון הנוכחי."}
        </div>
      ) : (
        <div className="space-y-2" data-testid="by-lead-list">
          {filtered.map((row) => (
            <LeadRow
              key={row.lead_id}
              row={row}
              expanded={!!expanded[row.lead_id]}
              onToggle={() => toggle(row.lead_id)}
              templateById={templateById}
            />
          ))}
        </div>
      )}
    </>
  );
}

function StatusChip({ active, onClick, children, color = "stone", testId }) {
  const activeCls = {
    stone:   "bg-brand-charcoal text-white border-brand-charcoal",
    emerald: "bg-emerald-600 text-white border-emerald-600",
    blue:    "bg-blue-600 text-white border-blue-600",
    amber:   "bg-amber-500 text-white border-amber-500",
    red:     "bg-rose-600 text-white border-rose-600",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-xs font-bold rounded-full px-3 py-1.5 border transition ${active ? activeCls[color] : "bg-white text-stone-600 border-stone-200 hover:border-stone-400"}`}
    >
      {children}
    </button>
  );
}

function FilterSelect({ label, value, onChange, children, testId }) {
  return (
    <div className="flex items-center gap-2 bg-stone-50 border border-stone-200 rounded-lg px-3 h-9">
      <span className="text-[10px] font-bold text-stone-500 uppercase tracking-wide">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="bg-transparent text-sm font-bold text-brand-charcoal focus:outline-none cursor-pointer max-w-[160px] truncate"
      >
        {children}
      </select>
    </div>
  );
}

function MiniStat({ label, value, sub, testId }) {
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-4 shadow-sm" data-testid={testId}>
      <div className="text-[10px] font-bold text-stone-500 uppercase tracking-wide mb-1">{label}</div>
      <div className="flex items-baseline gap-2">
        <div className="text-2xl font-[800] text-brand-charcoal">{value.toLocaleString("he-IL")}</div>
        {sub && <div className="text-xs font-bold text-emerald-700">{sub}</div>}
      </div>
    </div>
  );
}

function LeadRow({ row, expanded, onToggle, templateById }) {
  const openRate = row.totals.sent > 0 ? Math.round((row.totals.opened / row.totals.sent) * 100) : null;
  const hasOpened = row.totals.opened > 0;

  return (
    <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden" data-testid={`by-lead-row-${row.lead_id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full p-4 md:p-5 flex items-center gap-4 hover:bg-stone-50 transition text-right"
      >
        {/* Avatar */}
        <div className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 ${hasOpened ? "bg-emerald-50" : "bg-stone-100"}`}>
          <User className={`w-5 h-5 ${hasOpened ? "text-emerald-600" : "text-stone-400"}`} />
        </div>
        {/* Identity */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-brand-charcoal">{row.name || "(ללא שם)"}</span>
            {row.list_id && (
              <span className="text-[10px] font-bold text-stone-600 bg-stone-100 rounded-full px-2 py-0.5" dir="ltr">
                {row.list_id}
              </span>
            )}
          </div>
          <div className="text-xs text-stone-500 truncate ltr text-left" dir="ltr">{row.email || row.lead_id}</div>
        </div>
        {/* Stats */}
        <div className="hidden sm:flex items-center gap-4 md:gap-6">
          <StatBadge label="נשלחו" value={row.totals.sent} color="stone" />
          <StatBadge label="נפתחו" value={row.totals.opened} color="emerald" />
          {row.totals.failed > 0 && <StatBadge label="נכשלו" value={row.totals.failed} color="red" />}
          {openRate !== null && (
            <div className="text-center">
              <div className={`text-lg font-[800] ${openRate >= 30 ? "text-emerald-700" : openRate >= 10 ? "text-amber-700" : "text-stone-500"}`}>{openRate}%</div>
              <div className="text-[10px] text-stone-500 font-bold">Open rate</div>
            </div>
          )}
        </div>
        {/* Chevron */}
        <ChevronDown className={`w-5 h-5 text-stone-400 transition-transform flex-shrink-0 ${expanded ? "rotate-180" : ""}`} />
      </button>

      {/* Mobile stats */}
      <div className="sm:hidden px-4 pb-3 flex gap-3 flex-wrap">
        <StatBadge label="נשלחו" value={row.totals.sent} color="stone" />
        <StatBadge label="נפתחו" value={row.totals.opened} color="emerald" />
        {row.totals.failed > 0 && <StatBadge label="נכשלו" value={row.totals.failed} color="red" />}
      </div>

      {/* Expanded — sends list */}
      {expanded && (
        <div className="border-t border-stone-100 bg-stone-50/50">
          {row.sends.length === 0 ? (
            <div className="p-6 text-center text-stone-400 text-xs">אין שליחות</div>
          ) : (
            <ul className="divide-y divide-stone-200/60">
              {row.sends.map((s) => (
                <li key={s.id} className="p-3 md:p-4 flex items-center gap-3 text-sm" data-testid={`send-row-${s.id}`}>
                  <StatusBadge status={s.opened_at ? "opened" : s.status} />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-brand-charcoal truncate">
                      {s.template_name || templateById[s.template_id]?.name || <span className="text-stone-400">{s.template_id}</span>}
                    </div>
                    {s.template_subject && (
                      <div className="text-xs text-stone-500 truncate">{s.template_subject}</div>
                    )}
                    {s.error && (
                      <div className="text-[11px] text-red-700 mt-0.5 truncate" title={s.error}>שגיאה: {s.error}</div>
                    )}
                  </div>
                  <div className="text-xs text-stone-500 text-left ltr flex-shrink-0 space-y-0.5">
                    {s.sent_at && <div>נשלח: {formatInAppTz(s.sent_at)}</div>}
                    {s.opened_at && <div className="text-emerald-700 font-bold">נפתח: {formatInAppTz(s.opened_at)}</div>}
                    {!s.sent_at && s.scheduled_for && <div className="text-amber-700">מתוזמן: {formatInAppTz(s.scheduled_for)}</div>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function StatBadge({ label, value, color }) {
  const colors = {
    stone:   "text-stone-700",
    emerald: "text-emerald-700",
    red:     "text-red-700",
    amber:   "text-amber-700",
  };
  return (
    <div className="text-center">
      <div className={`text-lg font-[800] ${colors[color] || colors.stone}`}>{value.toLocaleString("he-IL")}</div>
      <div className="text-[10px] text-stone-500 font-bold">{label}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const s = STATUS_BADGE[status] || { label: status || "—", cls: "bg-stone-50 text-stone-600 border-stone-200" };
  return (
    <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 border ${s.cls}`}>{s.label}</span>
  );
}


function SettingsPanel({ settings, setSettings }) {
  return (
    <div className="space-y-5 max-w-3xl">
      {/* Timezone */}
      <div className="bg-white rounded-2xl p-6 border border-stone-200" data-testid="tz-card">
        <div className="flex items-center gap-2 mb-4">
          <h3 className="font-[800] text-lg">אזור זמן (Timezone)</h3>
          <span className="text-[10px] font-bold text-brand-amber bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">משפיע על תזמון</span>
        </div>
        <p className="text-xs text-stone-500 mb-4 leading-[1.7]">
          כל השעות בטופסי התזמון (Broadcast, Fixed-date templates) מתפרשות באזור הזמן הזה — לא בזמן המקומי של הדפדפן. במסד הנתונים הכל נשמר כ-UTC. לשינוי: הגדר משתנה סביבה <code className="ltr bg-stone-100 px-1.5 py-0.5 rounded text-[11px]">EMAIL_TIMEZONE</code> בבקאנד.
        </p>
        <div className="space-y-3 text-sm">
          <Row label="IANA Timezone">
            <code className="text-xs bg-stone-100 px-2 py-1 rounded ltr" data-testid="tz-name">{settings.timezone || "—"}</code>
          </Row>
          <Row label="Offset מ-UTC">
            <span className="ltr font-mono text-emerald-700 font-bold" data-testid="tz-offset">{settings.timezone_offset || "—"}</span>
            {settings.timezone_abbrev && <span className="ms-2 text-xs text-stone-500 ltr">({settings.timezone_abbrev})</span>}
          </Row>
          <Row label="שעה נוכחית בשרת">
            <span className="ltr font-mono text-stone-700" data-testid="tz-current">{settings.server_time_local || "—"}</span>
          </Row>
        </div>
      </div>

      {/* Resend */}
      <SenderSettingsCard settings={settings} onUpdated={(s) => setSettings((prev) => ({ ...prev, ...s }))} />

      {/* Business identity — appears in every commercial email footer */}
      <div className="bg-white rounded-2xl p-6 border border-stone-200">
        <div className="flex items-center gap-2 mb-4">
          <h3 className="font-[800] text-lg">פרטי העסק (Footer קשיח)</h3>
          <span className="text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">חובה חוקית</span>
        </div>
        <p className="text-xs text-stone-500 mb-4 leading-[1.7]">
          פרטים אלו יופיעו אוטומטית בסיום כל מייל שיווקי, לצד קישור להסרה. הם נדרשים ע&quot;פ חוק התקשורת §30א(ז) ואסור להסתירם או להסירם.
        </p>
        <div className="space-y-3 text-sm">
          <Row label="שם עסק">{settings.business_name || <span className="text-red-500">חסר!</span>}</Row>
          <Row label="ע.מ / ח.פ">{settings.business_id || <span className="text-red-500">חסר!</span>}</Row>
          <Row label="כתובת פיזית">{settings.business_address || <span className="text-red-500">חסר!</span>}</Row>
          <Row label="טלפון">{settings.business_phone || <span className="text-stone-400">לא מוגדר</span>}</Row>
          <Row label="דף יצירת קשר">
            <code className="text-xs bg-stone-100 px-2 py-1 rounded ltr">{settings.business_contact_url || "—"}</code>
          </Row>
        </div>

        {/* Footer preview */}
        {settings.business_name && (
          <div className="mt-6 border-t border-stone-200 pt-4">
            <div className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">תצוגה מקדימה של ה-Footer</div>
            <div className="bg-stone-50 rounded-xl p-4 border border-stone-200" dir="rtl">
              <hr className="border-stone-200 my-4" />
              <div className="text-xs text-stone-500 space-y-1.5 text-right">
                <div>
                  <strong className="text-stone-800">{settings.business_name}</strong>
                  {settings.business_id && ` · ע.מ / ח.פ: ${settings.business_id}`}
                  {settings.business_address && ` · ${settings.business_address}`}
                  {settings.business_phone && ` · טל׳: ${settings.business_phone}`}
                </div>
                <div>קיבלת הודעה זו לאחר שנרשמת מרצונך באחד מדפי הנחיתה של האתר.</div>
                <div><span className="text-red-600 font-bold underline">להסרה מרשימת התפוצה לחצו כאן</span></div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Compliance summary */}
      <div className="bg-white rounded-2xl p-6 border border-stone-200">
        <h3 className="font-[800] text-lg mb-4">בקרות רגולציה פעילות</h3>
        <ul className="space-y-2 text-sm">
          <ComplianceItem ok>[פרסומת עבורך] מתווסף אוטומטית לסוף הנושא של כל מייל שיווקי</ComplianceItem>
          <ComplianceItem ok>Footer חובה מוזרק ולא ניתן לעריכה</ComplianceItem>
          <ComplianceItem ok>List-Unsubscribe + One-Click Headers (RFC 8058)</ComplianceItem>
          <ComplianceItem ok>לינק הסרה בכל מייל שיווקי (חוק התקשורת §30א)</ComplianceItem>
          <ComplianceItem ok>consent_status נבדק לפני כל שליחה</ComplianceItem>
          <ComplianceItem ok>opt_in_ip + opt_in_at נשמרים לכל ליד (ראייה משפטית)</ComplianceItem>
        </ul>
      </div>

      {/* Update instructions */}
      <div className="bg-white rounded-2xl p-6 border border-stone-200">
        <h3 className="font-[800] text-sm uppercase tracking-wider text-stone-500 mb-3">איך לעדכן פרטי עסק</h3>
        <ol className="list-decimal ms-6 space-y-1 text-sm text-stone-700 leading-[1.7]">
          <li>פתח <code className="px-1 bg-stone-100 rounded">/app/backend/.env</code></li>
          <li>עדכן: <code className="px-1 bg-stone-100 rounded">BUSINESS_NAME</code>, <code className="px-1 bg-stone-100 rounded">BUSINESS_ID</code>, <code className="px-1 bg-stone-100 rounded">BUSINESS_ADDRESS</code>, <code className="px-1 bg-stone-100 rounded">BUSINESS_PHONE</code>, <code className="px-1 bg-stone-100 rounded">BUSINESS_CONTACT_URL</code></li>
          <li>הרץ: <code className="px-1 bg-stone-100 rounded">sudo supervisorctl restart backend</code></li>
        </ol>
      </div>
    </div>
  );
}

function ComplianceItem({ ok, children }) {
  return (
    <li className="flex items-start gap-2">
      {ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />}
      <span className="text-stone-700">{children}</span>
    </li>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between border-b border-stone-100 pb-2">
      <span className="text-stone-500">{label}</span>
      <span className="font-bold">{children}</span>
    </div>
  );
}

function SenderSettingsCard({ settings, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [fromEmail, setFromEmail] = useState(settings.from_email || "");
  const [fromName, setFromName] = useState(settings.from_name || "");
  const [saving, setSaving] = useState(false);

  const openEdit = () => {
    setFromEmail(settings.from_email || "");
    setFromName(settings.from_name || "");
    setEditing(true);
  };

  const cancel = () => setEditing(false);

  const save = async (e) => {
    e?.preventDefault();
    const email = fromEmail.trim();
    const name = fromName.trim();
    if (!email) {
      toast.error("כתובת שולח היא שדה חובה");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("כתובת מייל לא תקינה");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/admin/email/settings/sender", {
        from_email: email,
        from_name: name,
      });
      toast.success("ההגדרות נשמרו");
      onUpdated?.(data);
      setEditing(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "שמירה נכשלה");
    } finally {
      setSaving(false);
    }
  };

  const revertToEnv = async () => {
    if (!window.confirm("לחזור לערכי ברירת המחדל של הסביבה (.env)?")) return;
    setSaving(true);
    try {
      // Clear DB override — send empty strings
      const { data } = await api.put("/admin/email/settings/sender", {
        from_email: settings.env_from_email || "",
        from_name: settings.env_from_name || "",
      });
      toast.success("חזרנו לערכי ה-.env");
      onUpdated?.({ ...data, from_source: "env" });
      setEditing(false);
    } catch (err) {
      toast.error("החזרה נכשלה");
    } finally {
      setSaving(false);
    }
  };

  const isDbOverride = settings.from_source === "db";

  return (
    <div className="bg-white rounded-2xl p-6 border border-stone-200" data-testid="sender-settings-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="font-[800] text-lg">Resend / SMTP</h3>
          {isDbOverride && (
            <span className="text-[10px] font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">
              נערך מהאדמין
            </span>
          )}
        </div>
        {!editing && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="sender-edit-btn"
            onClick={openEdit}
            className="text-brand-amber hover:bg-amber-50 h-8"
          >
            <Edit3 className="w-4 h-4 ms-1" />
            עריכה
          </Button>
        )}
      </div>

      {editing ? (
        <form onSubmit={save} className="space-y-4" data-testid="sender-edit-form">
          <div>
            <Label htmlFor="from_email" className="text-sm font-bold text-stone-700 mb-1.5 block">
              כתובת שולח (From) <span className="text-rose-500">*</span>
            </Label>
            <Input
              id="from_email"
              data-testid="sender-from-email"
              type="email"
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
              placeholder="team@emetohovot.co.il"
              className="h-11 ltr text-left"
              required
              autoFocus
            />
            <p className="text-xs text-stone-500 mt-1 leading-[1.6]">
              הדומיין חייב להיות מאומת ב-Resend. ההמלצה: לא <code className="bg-stone-100 px-1 rounded ltr">noreply@</code> — עדיף כתובת ידידותית (למשל <code className="bg-stone-100 px-1 rounded ltr">team@</code>) שמאותת על תקשורת דו-כיוונית.
            </p>
          </div>
          <div>
            <Label htmlFor="from_name" className="text-sm font-bold text-stone-700 mb-1.5 block">
              שם השולח
            </Label>
            <Input
              id="from_name"
              data-testid="sender-from-name"
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              placeholder="אמת או חובות"
              className="h-11"
              maxLength={80}
            />
            <p className="text-xs text-stone-500 mt-1">
              נצפה בתיבת הדואר של הנמען. השאירו ריק כדי להראות את הכתובת בלבד.
            </p>
          </div>

          {/* Live preview */}
          <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 ltr text-left" data-testid="sender-preview">
            <div className="text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-1 text-right" dir="rtl">
              תצוגה מקדימה של From
            </div>
            <div className="font-mono text-sm text-stone-700 break-all">
              {fromName.trim() ? `${fromName.trim()} <${fromEmail.trim() || "—"}>` : (fromEmail.trim() || "—")}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button
              type="submit"
              disabled={saving}
              data-testid="sender-save-btn"
              className="bg-brand-amber hover:bg-brand-amberHover text-white font-bold h-10 rounded-full px-5"
            >
              <Save className="w-4 h-4 ms-1" />
              {saving ? "שומר..." : "שמור"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={cancel}
              disabled={saving}
              data-testid="sender-cancel-btn"
              className="h-10"
            >
              <X className="w-4 h-4 ms-1" />
              ביטול
            </Button>
            {isDbOverride && (
              <Button
                type="button"
                variant="ghost"
                onClick={revertToEnv}
                disabled={saving}
                data-testid="sender-revert-btn"
                className="h-10 text-stone-500 hover:text-brand-charcoal me-auto"
                title={`חזרה לערכי .env (${settings.env_from_email})`}
              >
                <RotateCcw className="w-4 h-4 ms-1" />
                חזרה ל-.env
              </Button>
            )}
          </div>
        </form>
      ) : (
        <div className="space-y-3 text-sm">
          <Row label="Resend מחובר?">
            {settings.resend_api_key_set ? (
              <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-4 h-4" />כן</span>
            ) : (
              <span className="inline-flex items-center gap-1 text-amber-700"><XCircle className="w-4 h-4" />לא מוגדר</span>
            )}
          </Row>
          <Row label="כתובת שולח (From)">
            <code className="text-xs bg-stone-100 px-2 py-1 rounded ltr" data-testid="sender-current-email">{settings.from_email || "—"}</code>
          </Row>
          <Row label="שם השולח">
            <span data-testid="sender-current-name">{settings.from_name || <span className="text-stone-400">לא מוגדר</span>}</span>
          </Row>
          <Row label="תצוגה בתיבת הנמען">
            <code className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 px-2 py-1 rounded ltr" data-testid="sender-preview-static">
              {settings.from_name ? `${settings.from_name} <${settings.from_email}>` : (settings.from_email || "—")}
            </code>
          </Row>
        </div>
      )}
    </div>
  );
}
